"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { AgentsView } from "@/components/agents-view";
import { AppSidebar, AppTopbar, type AnalysisSaveState, workspaceLabel } from "@/components/app-shell-chrome";
import { BrowserLabView } from "@/components/browser-lab-view";
import { DefectsView } from "@/components/defects-view";
import { GeneratorView } from "@/components/generator-view";
import { IntegrationsView } from "@/components/integrations-view";
import { LoginView } from "@/components/login-view";
import { OverviewView } from "@/components/overview-view";
import { OrdersView } from "@/components/orders-view";
import { RunsView } from "@/components/runs-view";
import { TestPipelineStrip } from "@/components/test-pipeline-strip";
import { clearStoredCsrfToken, loadCurrentUser, loadStoredCsrfToken, login, logout, type AdminUser } from "@/lib/auth-api";
import { authErrorMessage, fallbackPipelineRun } from "@/lib/app-shell-helpers";
import { demoAgents, demoCases, demoOverview } from "@/lib/demo-data";
import {
    completePageTestRun,
    createPageTestRun,
    generatePageCases,
    loadAgents,
    loadLatestQualityAnalysis,
    loadOverview,
    loadQualityExecutionRuns,
    saveQualityAnalysis,
} from "@/lib/qa-api";
import type {
    Agent,
    GeneratedCase,
    NavKey,
    PageTestSource,
    QualityAnalysisSaveInput,
    QualityAnalysisStage,
    QualityOverview,
    RunPhase,
    TestRun,
} from "@/lib/qa-types";

type QualityAnalysisSnapshot = Omit<QualityAnalysisSaveInput, "analysis_id" | "expected_version">;
const pipelineStages = new Set<NavKey>(["browser", "generate", "runs", "overview"]);
const pipelineStepDurationMs = 3_000;

export function AppShell() {
    const [authChecking, setAuthChecking] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
    const [csrfToken, setCsrfToken] = useState<string | null>(null);
    const [active, setActive] = useState<NavKey>("browser");
    const [overview, setOverview] = useState<QualityOverview>(demoOverview);
    const [agents, setAgents] = useState<Agent[]>(demoAgents);
    const [source, setSource] = useState<"live" | "demo">("demo");
    const [runPhase, setRunPhase] = useState<RunPhase>("idle");
    const [isGenerating, setIsGenerating] = useState(false);
    const [cases, setCases] = useState<GeneratedCase[]>([]);
    const [pipelineSource, setPipelineSource] = useState<PageTestSource | null>(null);
    const [currentRun, setCurrentRun] = useState<TestRun | null>(null);
    const [executionRuns, setExecutionRuns] = useState<TestRun[]>([]);
    const [saveState, setSaveState] = useState<AnalysisSaveState>("idle");
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const accountMenuRef = useRef<HTMLDivElement>(null);
    const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
    const pipelineSequence = useRef(0);
    const analysisIdentity = useRef<{ analysisId: string; version: number } | null>(null);
    const saveQueue = useRef<Promise<void>>(Promise.resolve());
    const restoreLatestAnalysis = useCallback(async () => {
        try {
            const [restored, history] = await Promise.all([loadLatestQualityAnalysis(), loadQualityExecutionRuns()]);
            setExecutionRuns(history);
            if (!restored) {
                analysisIdentity.current = null;
                return;
            }
            analysisIdentity.current = { analysisId: restored.analysis_id, version: restored.version };
            setPipelineSource(restored.source);
            setCases(restored.cases);
            setCurrentRun(restored.run);
            setRunPhase(restored.run_phase);
            if (restored.report) setOverview(restored.report.overview);
            setActive(restored.active_stage);
            setSaveState("saved");
        } catch {
            setSaveState("error");
        }
    }, []);
    useEffect(() => {
        void loadCurrentUser()
            .then(async (user) => {
                setCurrentUser(user);
                setCsrfToken(user ? loadStoredCsrfToken() : null);
                if (user) setActive("browser");
                if (!user) clearStoredCsrfToken();
                if (user) await restoreLatestAnalysis();
            })
            .catch((error: unknown) => setAuthError(authErrorMessage(error)))
            .finally(() => setAuthChecking(false));
        void Promise.all([loadOverview(), loadAgents()]).then(([overviewResult, agentResult]) => {
            setOverview(overviewResult.data);
            setAgents(agentResult.data);
            setSource(overviewResult.source === "live" && agentResult.source === "live" ? "live" : "demo");
        });
        return () => {
            pipelineSequence.current += 1;
            timers.current.forEach(clearTimeout);
        };
    }, [restoreLatestAnalysis]);
    useEffect(() => {
        if (!accountMenuOpen) return;
        function closeOnOutsideClick(event: PointerEvent) {
            if (!accountMenuRef.current?.contains(event.target as Node)) setAccountMenuOpen(false);
        }
        function closeOnEscape(event: KeyboardEvent) {
            if (event.key === "Escape") setAccountMenuOpen(false);
        }
        document.addEventListener("pointerdown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("pointerdown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [accountMenuOpen]);
    async function handleLogin(username: string, password: string) {
        setAuthError(null);
        try {
            const session = await login(username, password);
            setCsrfToken(session.csrf_token);
            setActive("browser");
            await restoreLatestAnalysis();
            setCurrentUser(session.user);
        } catch (error) {
            setAuthError(authErrorMessage(error));
        }
    }

    async function handleLogout() {
        setAuthError(null);
        setAccountMenuOpen(false);
        try {
            await logout(csrfToken);
        } catch (error) {
            setAuthError(authErrorMessage(error));
            return;
        }
        clearStoredCsrfToken();
        setCurrentUser(null);
        setCsrfToken(null);
        setPipelineSource(null);
        setCases([]);
        setCurrentRun(null);
        setExecutionRuns([]);
        setRunPhase("idle");
        setSaveState("idle");
        analysisIdentity.current = null;
    }

    function queueAnalysisSave(snapshot: QualityAnalysisSnapshot): Promise<void> {
        if (!csrfToken) return Promise.resolve();
        setSaveState("saving");
        const queued = saveQueue.current.then(async () => {
            const identity = analysisIdentity.current;
            try {
                const saved = await saveQualityAnalysis(csrfToken, {
                    ...snapshot,
                    ...(identity ? { analysis_id: identity.analysisId, expected_version: identity.version } : {}),
                });
                analysisIdentity.current = { analysisId: saved.analysis_id, version: saved.version };
                if (saved.run) setExecutionRuns((runs) => [saved.run!, ...runs.filter((run) => run.run_id !== saved.run!.run_id)]);
                setSaveState("saved");
            } catch {
                setSaveState("error");
            }
        });
        saveQueue.current = queued.catch(() => undefined);
        return queued;
    }

    function pipelineSnapshot(activeStage: QualityAnalysisStage): QualityAnalysisSnapshot | null {
        if (!pipelineSource) return null;
        return {
            active_stage: activeStage,
            run_phase: runPhase,
            source: pipelineSource,
            cases,
            run: currentRun,
            report: runPhase === "passed" ? { overview, case_count: cases.length, completed_at: new Date().toISOString() } : null,
        };
    }

    function navigateTo(target: NavKey) {
        setActive(target);
        if (!pipelineStages.has(target)) return;
        const snapshot = pipelineSnapshot(target as QualityAnalysisStage);
        if (snapshot) void queueAnalysisSave(snapshot);
    }

    function startRun() {
        timers.current.forEach(clearTimeout);
        setActive(active === "runs" ? "runs" : "overview");
        setRunPhase("queued");
        const sequence: [number, RunPhase][] = [
            [pipelineStepDurationMs, "running"],
            [pipelineStepDurationMs * 2, "healing"],
            [pipelineStepDurationMs * 3, "passed"],
        ];
        timers.current = sequence.map(([delay, phase]) => setTimeout(() => setRunPhase(phase), delay));
    }

    async function generateCases(requirement: string) {
        const source = pipelineSource ?? {
            target_url: "http://localhost:3000/",
            title: "手工需求场景",
            requirement,
            characteristics: ["来源为手工输入需求", "覆盖正向、逆向和边界场景"],
        };
        const updatedSource = { ...source, requirement: requirement.trim() };
        setPipelineSource(updatedSource);
        setIsGenerating(true);
        setCases([]);
        const generationDelay = waitForPipelineStep();
        let generated: GeneratedCase[];
        try {
            generated = await generatePageCases(updatedSource);
        } catch {
            generated = demoCases;
        }
        await generationDelay;
        try {
            setCases(generated);
            void queueAnalysisSave({
                active_stage: "generate",
                run_phase: "idle",
                source: updatedSource,
                cases: generated,
                run: null,
                report: null,
            });
        } finally {
            setIsGenerating(false);
        }
    }

    function acceptPageAnalysis(source: PageTestSource) {
        void runAutomatedPipeline(source);
    }

    async function runAutomatedPipeline(source: PageTestSource) {
        const sequence = pipelineSequence.current + 1;
        pipelineSequence.current = sequence;
        timers.current.forEach(clearTimeout);
        await saveQueue.current;
        analysisIdentity.current = null;
        setPipelineSource(source);
        setCases([]);
        setCurrentRun(null);
        setRunPhase("idle");
        setIsGenerating(false);
        void queueAnalysisSave({
            active_stage: "browser",
            run_phase: "idle",
            source,
            cases: [],
            run: null,
            report: null,
        });

        await waitForPipelineStep();
        if (pipelineSequence.current !== sequence) return;
        setActive("generate");
        setIsGenerating(true);
        void queueAnalysisSave({
            active_stage: "generate",
            run_phase: "idle",
            source,
            cases: [],
            run: null,
            report: null,
        });

        const generationDelay = waitForPipelineStep();
        let generated: GeneratedCase[];
        try {
            generated = await generatePageCases(source);
        } catch {
            generated = demoCases;
        }
        await generationDelay;
        if (pipelineSequence.current !== sequence) return;
        setCases(generated);
        setIsGenerating(false);
        void queueAnalysisSave({
            active_stage: "generate",
            run_phase: "idle",
            source,
            cases: generated,
            run: null,
            report: null,
        });

        await waitForPipelineStep();
        if (pipelineSequence.current !== sequence) return;
        setActive("runs");
        setRunPhase("queued");

        let created: TestRun;
        try {
            created = await createPageTestRun(
                source,
                generated.map((item) => item.case_id),
            );
        } catch {
            created = fallbackPipelineRun(source, generated, "RUNNING");
        }
        if (pipelineSequence.current !== sequence) return;
        setCurrentRun(created);
        void queueAnalysisSave({
            active_stage: "runs",
            run_phase: "running",
            source,
            cases: generated,
            run: created,
            report: null,
        });

        await waitForPipelineStep();
        if (pipelineSequence.current !== sequence) return;
        setRunPhase("running");
        await waitForPipelineStep();
        if (pipelineSequence.current !== sequence) return;
        setRunPhase("healing");
        await waitForPipelineStep();
        if (pipelineSequence.current !== sequence) return;

        let completed: TestRun;
        try {
            completed = await completePageTestRun(created.run_id);
        } catch {
            completed = { ...created, status: "PASSED" };
        }
        if (pipelineSequence.current !== sequence) return;
        setCurrentRun(completed);
        setRunPhase("passed");
        void queueAnalysisSave({
            active_stage: "overview",
            run_phase: "passed",
            source,
            cases: generated,
            run: completed,
            report: { overview, case_count: generated.length, completed_at: new Date().toISOString() },
        });

        await waitForPipelineStep();
        if (pipelineSequence.current === sequence) setActive("overview");
    }

    function waitForPipelineStep(): Promise<void> {
        return new Promise((resolve) => {
            const timer = setTimeout(resolve, pipelineStepDurationMs);
            timers.current.push(timer);
        });
    }
    async function executeGeneratedCases() {
        if (!pipelineSource || cases.length === 0) return;
        await saveQueue.current;
        analysisIdentity.current = null;
        timers.current.forEach(clearTimeout);
        setActive("runs");
        setCurrentRun(null);
        setRunPhase("queued");
        try {
            const created = await createPageTestRun(
                pipelineSource,
                cases.map((item) => item.case_id),
            );
            setCurrentRun(created);
            void queueAnalysisSave({
                active_stage: "runs",
                run_phase: "running",
                source: pipelineSource,
                cases,
                run: created,
                report: null,
            });
            timers.current = [
                setTimeout(() => setRunPhase("running"), pipelineStepDurationMs),
                setTimeout(() => setRunPhase("healing"), pipelineStepDurationMs * 2),
                setTimeout(() => void finishPipelineRun(created.run_id), pipelineStepDurationMs * 3),
            ];
        } catch {
            startRun();
            setActive("runs");
        }
    }
    async function finishPipelineRun(runId: string) {
        let completed: TestRun | null = null;
        try {
            completed = await completePageTestRun(runId);
            setCurrentRun(completed);
        } catch {
            completed = currentRun ? { ...currentRun, status: "PASSED" } : null;
            if (completed) setCurrentRun(completed);
        } finally {
            setRunPhase("passed");
            if (pipelineSource && completed) {
                void queueAnalysisSave({
                    active_stage: "overview",
                    run_phase: "passed",
                    source: pipelineSource,
                    cases,
                    run: completed,
                    report: { overview, case_count: cases.length, completed_at: new Date().toISOString() },
                });
            }
        }
    }

    async function refreshAgents() {
        const result = await loadAgents();
        setAgents(result.data);
    }

    if (authChecking || currentUser === null) {
        return <LoginView checking={authChecking} error={authError} onLogin={handleLogin} />;
    }

    return (
        <div className="app-shell">
            <AppSidebar
                active={active}
                currentUser={currentUser}
                executionCount={executionRuns.length}
                onLogout={() => void handleLogout()}
                onNavigate={navigateTo}
            />
            <main className="workspace">
                <AppTopbar
                    accountMenuOpen={accountMenuOpen}
                    accountMenuRef={accountMenuRef}
                    activeLabel={workspaceLabel(active)}
                    currentUser={currentUser}
                    dataSource={source}
                    onLogout={() => void handleLogout()}
                    onToggleAccountMenu={() => setAccountMenuOpen((open) => !open)}
                    saveState={saveState}
                    showSaveState={Boolean(pipelineSource)}
                />
                <div className="content">
                    {["browser", "generate", "runs", "overview"].includes(active) && (
                        <TestPipelineStrip
                            active={active}
                            caseCount={cases.length}
                            hasReport={currentRun?.status === "PASSED"}
                            onNavigate={navigateTo}
                            runPhase={runPhase}
                            source={pipelineSource}
                        />
                    )}
                    {active === "overview" && (
                        <OverviewView
                            caseCount={cases.length}
                            run={currentRun}
                            runPhase={runPhase}
                            onOpenGenerator={() => navigateTo("generate")}
                            onOpenDefects={() => navigateTo("defects")}
                            runs={executionRuns}
                            source={pipelineSource}
                        />
                    )}
                    {active === "orders" && <OrdersView csrfToken={csrfToken} roles={currentUser.roles} />}
                    {active === "defects" && <DefectsView runs={executionRuns} />}
                    {active === "generate" && (
                        <GeneratorView
                            cases={cases}
                            isGenerating={isGenerating}
                            key={`${pipelineSource?.target_url ?? "manual"}:${pipelineSource?.requirement ?? "default"}`}
                            onExecute={() => void executeGeneratedCases()}
                            onGenerate={(requirement) => void generateCases(requirement)}
                            source={pipelineSource}
                        />
                    )}
                    {active === "browser" && (
                        <BrowserLabView csrfToken={csrfToken} onCreateScenarios={acceptPageAnalysis} restoredSource={pipelineSource} />
                    )}
                    {active === "runs" && (
                        <RunsView
                            onOpenOverview={() => navigateTo("overview")}
                            onStart={() => (pipelineSource && cases.length ? void executeGeneratedCases() : startRun())}
                            phase={runPhase}
                            run={currentRun}
                            source={pipelineSource}
                        />
                    )}
                    {active === "agents" && <AgentsView agents={agents} onRegister={() => navigateTo("integrations")} />}
                    {active === "integrations" && <IntegrationsView csrfToken={csrfToken} onAgentSaved={refreshAgents} />}
                </div>
            </main>
        </div>
    );
}
